import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getTeamMembers } from '@/services/team.service';
import type { TeamMember } from '@/types/team';

import { getDisplayName } from '../utils/format';

/**
 * Safely extracts a string ID from various formats.
 * Handles: plain strings, MongoDB Extended JSON { $oid: "..." }, objects with toString().
 */
function extractStringId(id: unknown): string {
  if (typeof id === 'string') {
    return id;
  }
  // Handle MongoDB Extended JSON format: { $oid: "..." }
  if (id && typeof id === 'object' && '$oid' in id) {
    const oid = (id as { $oid: unknown }).$oid;
    if (typeof oid === 'string') {
      return oid;
    }
  }
  // Handle objects with _id property (nested ObjectId)
  if (id && typeof id === 'object' && '_id' in id) {
    return extractStringId((id as { _id: unknown })._id);
  }
  // Fallback - log warning for debugging
  console.warn('[TeamMembersContext] Unexpected id format:', id);
  return '';
}

interface TeamMemberOption {
  id: string;
  name: string;
}

interface TeamMembersContextValue {
  members: TeamMemberOption[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const TeamMembersContext = createContext<TeamMembersContextValue | null>(null);

interface TeamMembersProviderProps {
  children: ReactNode;
}

export function TeamMembersProvider({ children }: TeamMembersProviderProps) {
  const [members, setMembers] = useState<TeamMemberOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await getTeamMembers();

    if (!result.success) {
      setError(result.error.message || 'Failed to fetch team members');
      setIsLoading(false);
      return;
    }

    const filteredMembers = result.data
      .filter((member: TeamMember) =>
        member.role.some((r) => r === 'admin' || r === 'agent')
      )
      .map((member: TeamMember) => ({
        id: extractStringId(member.id),
        name: getDisplayName(member),
      }));

    setMembers(filteredMembers);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchMembers();
  }, [fetchMembers]);

  const value: TeamMembersContextValue = {
    members,
    isLoading,
    error,
    refetch: fetchMembers,
  };

  return (
    <TeamMembersContext.Provider value={value}>
      {children}
    </TeamMembersContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTeamMembersContext(): TeamMembersContextValue {
  const context = useContext(TeamMembersContext);
  if (!context) {
    throw new Error(
      'useTeamMembersContext must be used within a TeamMembersProvider'
    );
  }
  return context;
}
